import { useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import RefundTransferService from './RefundTransferService';

const useRefundTransfer = () => {
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchedProfile, setSearchedProfile] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const shopName = useSelector((state) => state.shop.name);

  const fetchRecords = async () => {
    setIsLoading(true);
    try {
      const res = await RefundTransferService.getList(shopName);
      setRecords(res.data);
    } catch (err) {
      toast.error('ดึงข้อมูลไม่สำเร็จ');
    } finally {
      setIsLoading(false);
    }
  };

  const createRecord = async (data) => {
    try {
      const res = await RefundTransferService.create({ ...data, shop: shopName });
      await fetchRecords();
      return res.data;
    } catch (err) {
      toast.error(err.response?.data?.message || 'สร้างรายการไม่สำเร็จ');
      return false;
    }
  };

  const saveBankInfo = async (id, bankInfo) => {
    try {
      const res = await RefundTransferService.updateBankInfo(id, bankInfo);
      if (res.data?.lineNotified === false) toast.warning('บันทึกแล้ว แต่ส่ง LINE หาหัวหน้าไม่สำเร็จ — ระบบจะลองส่งซ้ำอัตโนมัติทุกชั่วโมง');
      await fetchRecords();
      return true;
    } catch (err) {
      toast.error('บันทึกข้อมูลธนาคารไม่สำเร็จ');
      return false;
    }
  };

  const uploadBankQr = async (id, file) => {
    const formData = new FormData();
    formData.append('qr', file);
    try {
      const res = await RefundTransferService.uploadBankQr(id, formData);
      if (res.data?.lineNotified === false) toast.warning('บันทึกแล้ว แต่ส่ง LINE หาหัวหน้าไม่สำเร็จ — ระบบจะลองส่งซ้ำอัตโนมัติทุกชั่วโมง');
      await fetchRecords();
      return true;
    } catch (err) {
      toast.error('อัปโหลด QR ไม่สำเร็จ');
      return false;
    }
  };

  const markCustomerNotified = async (id) => {
    try {
      await RefundTransferService.markCustomerNotified(id);
      await fetchRecords();
      return true;
    } catch (err) {
      toast.error(err.response?.data?.message || 'บันทึกไม่สำเร็จ');
      return false;
    }
  };

  // Claim a transferred refund so only one admin notifies the customer. Returns
  // true if we got the lock; on 409 the server message ("taken by X") is shown.
  const claimRefund = async (id, takeover = false) => {
    try {
      await RefundTransferService.claimRefund(id, takeover);
      await fetchRecords();
      return true;
    } catch (err) {
      toast.error(err.response?.data?.message || 'รับเคสไม่สำเร็จ');
      return false;
    }
  };

  const releaseRefund = async (id) => {
    try {
      await RefundTransferService.releaseRefund(id);
      await fetchRecords();
    } catch (err) { /* release is best-effort */ }
  };

  const revertToPending = async (id) => {
    try {
      await RefundTransferService.revertToPending(id);
      await fetchRecords();
      return true;
    } catch (err) {
      toast.error('ย้อนกลับไม่สำเร็จ');
      return false;
    }
  };

  const searchByUrl = async (url, customerId) => {
    try {
      const res = await RefundTransferService.searchByUrl(url, shopName, customerId);
      return res.data;
    } catch (err) {
      toast.error('ค้นหาจาก Link ไม่สำเร็จ');
      return null;
    }
  };

  // Soft-delete a refund (reason required by the backend). Refresh the list on success.
  const deleteRecord = async (id, reason) => {
    try {
      await RefundTransferService.deleteRefund(id, reason);
      await fetchRecords();
      return true;
    } catch (err) {
      toast.error(err.response?.data?.message || 'ลบรายการไม่สำเร็จ');
      return false;
    }
  };

  // Customer changed their mind: convert the refund to store credit. The backend
  // credits advanceAmount and soft-deletes the row (blocked once the money moved).
  const convertToCredit = async (id, note) => {
    try {
      const res = await RefundTransferService.convertToCredit(id, note);
      await fetchRecords();
      return res.data;
    } catch (err) {
      toast.error(err.response?.data?.message || 'เก็บเป็นยอดฝากไม่สำเร็จ');
      return null;
    }
  };

  // Edit a refund amount (backend blocks it once the money has moved).
  const updateAmount = async (id, amount) => {
    try {
      await RefundTransferService.updateAmount(id, amount);
      await fetchRecords();
      return true;
    } catch (err) {
      toast.error(err.response?.data?.message || 'แก้ไขยอดไม่สำเร็จ');
      return false;
    }
  };

  // Trace the refund flow (live + deleted) for an order URL — used in the report audit.
  const searchFlow = async (url) => {
    try {
      const res = await RefundTransferService.searchFlow(url, shopName);
      return res.data;
    } catch (err) {
      toast.error('ค้นหาจาก Link ไม่สำเร็จ');
      return null;
    }
  };

  const fetchReport = async () => {
    try {
      const res = await RefundTransferService.getReport(shopName);
      return res.data;
    } catch (err) {
      toast.error('ดึงรายงานไม่สำเร็จ');
      return null;
    }
  };

  const searchCustomer = async (smileName) => {
    setIsSearching(true);
    setSearchedProfile(null);
    try {
      const res = await RefundTransferService.searchCustomer(smileName, shopName);
      setSearchedProfile(res.data);
      return res.data;
    } catch (err) {
      toast.error('ค้นหาลูกค้าไม่สำเร็จ ตรวจสอบ Smile Name อีกครั้ง');
      return null;
    } finally {
      setIsSearching(false);
    }
  };

  return {
    records,
    isLoading,
    searchedProfile,
    isSearching,
    fetchRecords,
    createRecord,
    saveBankInfo,
    uploadBankQr,
    revertToPending,
    claimRefund,
    releaseRefund,
    markCustomerNotified,
    fetchReport,
    searchByUrl,
    deleteRecord,
    updateAmount,
    convertToCredit,
    searchFlow,
    searchCustomer,
    setSearchedProfile,
  };
};

export default useRefundTransfer;
